import React, { useEffect, useState, useMemo } from 'react';
import {
  Activity, AlertTriangle, CheckCircle, XCircle, RefreshCw,
  Shield, TrendingDown, Loader, Eye, MessageSquare,
  Calendar, Ban, Filter, Search,
  TrendingUp, DollarSign, Zap, Clock, Users, ChevronLeft, ChevronRight,
  Hash // ✅ Icône compteur
} from 'lucide-react';
import SMSModal from '../components/SMSModal';
import { monitoringService } from '../services/api';

const MonitoringClients = () => {
  const [dashboard, setDashboard] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('risk');

  // ✅ NOUVEAU: Mode de recherche
  const [searchMode, setSearchMode] = useState('client'); // 'client' ou 'meter'

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [smsCustomer, setSmsCustomer] = useState(null);
  const [showBulkSMSModal, setShowBulkSMSModal] = useState(false);
  const [bulkSMSRisk, setBulkSMSRisk] = useState(null);

  const [debouncedSearch, setDebouncedSearch] = useState('');

  // ✅ Debounce à 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== debouncedSearch) {
        setDebouncedSearch(searchTerm);
        setCurrentPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // ✅ Reset recherche quand on change de mode
  const handleSearchModeChange = (mode) => {
    setSearchMode(mode);
    setSearchTerm('');
    setDebouncedSearch('');
    setCurrentPage(1);
  };

  const handleSendSMS = (customer) => {
    setSmsCustomer(customer);
    setShowSMSModal(true);
  };

  const sendMonitoringSMS = async (message) => {
    try {
      const response = await monitoringService.sendMonitoringSMS(
        smsCustomer.customer_id,
        message
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const handleBulkSMSByRisk = (riskLevel) => {
    setBulkSMSRisk(riskLevel);
    setShowBulkSMSModal(true);
  };

  const sendBulkMonitoringSMS = async (message) => {
    try {
      const response = await monitoringService.sendMonitoringBulkSMS({
        risk_level: bulkSMSRisk || filter,
        message: message,
        use_template: false
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const getRiskCount = (level) => {
    if (!dashboard) return 0;
    return dashboard.by_risk_score?.[level] || 0;
  };

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const res = await monitoringService.getDashboard();
        setDashboard(res.data);
      } catch (e) {
        console.error(e);
      }
    };
    loadDashboard();
  }, []);

  useEffect(() => {
    const fetchCustomers = async () => {
      setLoading(true);
      try {
        const offset = (currentPage - 1) * itemsPerPage;
        const riskLevel = filter === 'all' ? null : filter;

        // ✅ Params explicites selon le mode de recherche
        const params = {
          risk_level: riskLevel,
          limit: itemsPerPage,
          offset: offset,
          sort_by: sortBy,
          // Mode client : search normal
          search: searchMode === 'client' && debouncedSearch ? debouncedSearch : undefined,
          // Mode compteur : meter_search (numéro ex: 5810262)
          meter_search: searchMode === 'meter' && debouncedSearch ? debouncedSearch : undefined,
        };

        const res = await monitoringService.getCustomersByRisk(
          params.risk_level,
          params.limit,
          params.offset,
          params.search || null,
          params.sort_by,
          params.meter_search || null
        );

        setCustomers(res.data.customers || []);
        setTotalItems(res.data.total || 0);
      } catch (e) {
        console.error('Erreur fetchCustomers:', e);
        setCustomers([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, [filter, currentPage, debouncedSearch, sortBy, searchMode]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, debouncedSearch, sortBy, searchMode]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUpdateScores = async () => {
    setLoading(true);
    try {
      await monitoringService.updateScores();
      alert('✅ Scores mis à jour avec succès!');
      setDashboard(null);
      setCurrentPage(1);
    } catch (error) {
      alert('❌ Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockInactive = async () => {
    if (!window.confirm('Bloquer tous les clients inactifs depuis plus de 60 jours ?')) return;
    setLoading(true);
    try {
      const result = await monitoringService.blockInactive(60);
      alert(`✅ ${result.data.blocked_count} clients bloqués`);
      setDashboard(null);
      setCurrentPage(1);
    } catch (error) {
      alert('❌ Erreur lors du blocage');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (action, customer) => {
    switch (action) {
      case 'sms':
        alert(`Envoi SMS à ${customer.full_name}`);
        break;
      case 'block':
        if (window.confirm(`Bloquer ${customer.full_name} ?`)) {
          alert(`Client ${customer.customer_id} bloqué`);
        }
        break;
    }
  };

  const getRiskBadge = (score) => {
    const badges = {
      actif: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle, label: 'Actif' },
      a_surveiller: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: AlertTriangle, label: 'À surveiller' },
      risque_eleve: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Risque élevé' }
    };
    const badge = badges[score] || badges.actif;
    const Icon = badge.icon;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${badge.bg} ${badge.text}`}>
        <Icon className="w-4 h-4" />
        {badge.label}
      </span>
    );
  };

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-lg bg-${color}-100 flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
    </div>
  );

  const getPageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 7;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  }, [currentPage, totalPages]);

  // ✅ Placeholder selon le mode
  const getSearchPlaceholder = () => {
    if (searchMode === 'meter') return '🔌 N° compteur (ex: 5810262, 22000...)';
    return '🔍 Nom, ID client, téléphone...';
  };

  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">

      {/* Modales SMS */}
      <SMSModal
        isOpen={showSMSModal}
        onClose={() => { setShowSMSModal(false); setSmsCustomer(null); }}
        onSend={sendMonitoringSMS}
        customer={smsCustomer}
      />
      <SMSModal
        isOpen={showBulkSMSModal}
        onClose={() => { setShowBulkSMSModal(false); setBulkSMSRisk(null); }}
        onSend={sendBulkMonitoringSMS}
        bulkMode={true}
        bulkCount={getRiskCount(bulkSMSRisk || filter)}
        riskLevel={bulkSMSRisk || filter}
        customer={customers[0]}
        templates={[
          {
            id: 'risk_alert', name: 'Alerte Risque Élevé',
            content: `MAD POWER - ALERTE URGENTE\nBonjour {name},\nVotre compte est en risque eleve.\nAction immediate requise.\nContactez-nous: [PHONE]\nMAD POWER`
          },
          {
            id: 'warning', name: 'Avertissement',
            content: `MAD POWER - Avertissement\nBonjour {name},\nVotre compte necessite attention.\nEvitez la suspension.\nRechargez maintenant.\nMAD POWER`
          },
          {
            id: 'payment_reminder', name: 'Rappel Paiement',
            content: `MAD POWER - Rappel\nBonjour {name},\nRedevances impayees detectees.\nMerci de regulariser.\nMAD POWER`
          }
        ]}
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-3 rounded-lg backdrop-blur-sm">
              <Activity className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Surveillance Automatique des Clients</h1>
              <p className="text-purple-100 text-sm mt-1">
                Recherche temps réel sur {dashboard?.total_customers?.toLocaleString() || '10000+'} clients
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleBulkSMSByRisk('risque_eleve')}
              disabled={loading || getRiskCount('risque_eleve') === 0}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all flex items-center gap-2 font-semibold shadow-md hover:shadow-lg disabled:opacity-50"
            >
              <MessageSquare className="w-5 h-5" />
              SMS Risque ({getRiskCount('risque_eleve')})
            </button>
            <button
              onClick={() => handleBulkSMSByRisk('a_surveiller')}
              disabled={loading || getRiskCount('a_surveiller') === 0}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-all flex items-center gap-2 font-semibold shadow-md hover:shadow-lg disabled:opacity-50"
            >
              <MessageSquare className="w-5 h-5" />
              SMS Alerte ({getRiskCount('a_surveiller')})
            </button>
            <button
              onClick={handleUpdateScores}
              disabled={loading}
              className="px-4 py-2 bg-white text-purple-600 rounded-lg hover:bg-purple-50 transition-all flex items-center gap-2 font-semibold shadow-md hover:shadow-lg disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
            <button
              onClick={handleBlockInactive}
              disabled={loading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all flex items-center gap-2 font-semibold shadow-md hover:shadow-lg disabled:opacity-50"
            >
              <Shield className="w-5 h-5" />
              Bloquer inactifs
            </button>
          </div>
        </div>
      </div>

      {/* Stats Dashboard */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard title="Total Clients" value={dashboard.total_customers?.toLocaleString() || 0} icon={Users} color="blue" />
          <StatCard title="Actifs" value={dashboard.by_risk_score?.actif?.toLocaleString() || 0} icon={CheckCircle} color="green" subtitle="Comportement normal" />
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">À Surveiller</p>
                <p className="text-2xl font-bold text-yellow-600">{dashboard.by_risk_score?.a_surveiller?.toLocaleString() || 0}</p>
                <button onClick={() => handleBulkSMSByRisk('a_surveiller')} disabled={getRiskCount('a_surveiller') === 0} className="mt-2 text-xs text-yellow-700 hover:text-yellow-900 underline flex items-center gap-1 disabled:opacity-50">
                  <MessageSquare size={12} />SMS groupé
                </button>
              </div>
              <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Risque Élevé</p>
                <p className="text-2xl font-bold text-red-600">{dashboard.by_risk_score?.risque_eleve?.toLocaleString() || 0}</p>
                <button onClick={() => handleBulkSMSByRisk('risque_eleve')} disabled={getRiskCount('risque_eleve') === 0} className="mt-2 text-xs text-red-700 hover:text-red-900 underline flex items-center gap-1 disabled:opacity-50">
                  <MessageSquare size={12} />SMS urgent
                </button>
              </div>
              <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
          <StatCard title="Impayés Total" value={(dashboard.total_unpaid_fees || 0).toLocaleString()} icon={TrendingDown} color="orange" subtitle="FCFA" />
        </div>
      )}

      {/* ✅ BLOC RECHERCHE AMÉLIORÉ */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          {/* ✅ Recherche avec sélecteur de mode */}
          <div className="md:col-span-1">
            {/* Sélecteur de mode de recherche */}
            <div className="flex rounded-lg border border-gray-300 overflow-hidden mb-2">
              <button
                onClick={() => handleSearchModeChange('client')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold transition-colors ${
                  searchMode === 'client'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Users className="w-3.5 h-3.5" />
                Client
              </button>
              <button
                onClick={() => handleSearchModeChange('meter')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold transition-colors border-l border-gray-300 ${
                  searchMode === 'meter'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Hash className="w-3.5 h-3.5" />
                Compteur
              </button>
            </div>

            {/* Champ de recherche */}
            <div className="relative">
              {searchMode === 'meter' ? (
                <Hash className={`w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 ${
                  searchTerm !== debouncedSearch ? 'text-purple-500 animate-pulse' : 'text-gray-400'
                }`} />
              ) : (
                <Search className={`w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 ${
                  searchTerm !== debouncedSearch ? 'text-purple-500 animate-pulse' : 'text-gray-400'
                }`} />
              )}
              <input
                type="text"
                placeholder={getSearchPlaceholder()}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                  searchMode === 'meter'
                    ? 'border-purple-300 focus:ring-purple-500 bg-purple-50'
                    : 'border-gray-300 focus:ring-purple-500'
                }`}
              />
              {searchTerm !== debouncedSearch && (
                <Loader className="w-4 h-4 absolute right-3 top-1/2 transform -translate-y-1/2 text-purple-500 animate-spin" />
              )}
              {searchTerm && searchTerm === debouncedSearch && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* ✅ Aide contextuelle selon le mode */}
            {searchMode === 'meter' && (
              <p className="mt-1 text-xs text-purple-600">
                🔌 Recherche les clients qui possèdent ce compteur
              </p>
            )}
          </div>

          {/* Filtre par risque */}
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Niveau de risque</label>
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-gray-500 flex-shrink-0" />
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="all">Tous les clients</option>
                  <option value="actif">✅ Actifs uniquement</option>
                  <option value="a_surveiller">⚠️ À surveiller</option>
                  <option value="risque_eleve">🔴 Risque élevé</option>
                </select>
              </div>
            </div>
          </div>

          {/* Tri */}
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Trier par</label>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="risk">🎯 Trier par risque</option>
                  <option value="last_purchase">📅 Dernier achat</option>
                  <option value="unpaid">💰 Impayés (+ élevé)</option>
                  <option value="name">🔤 Nom (A-Z)</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ Indicateurs de filtres actifs */}
        {(debouncedSearch || filter !== 'all') && (
          <div className="mt-3 flex flex-wrap gap-2">
            {debouncedSearch && (
              <div className={`px-3 py-1 rounded-full text-sm flex items-center gap-2 ${
                searchMode === 'meter'
                  ? 'bg-purple-100 border border-purple-300 text-purple-800'
                  : 'bg-purple-100 border border-purple-300 text-purple-800'
              }`}>
                {searchMode === 'meter' ? <Hash className="w-4 h-4" /> : <Search className="w-4 h-4" />}
                <span>
                  {searchMode === 'meter' ? 'Compteur: ' : 'Recherche: '}
                  <strong>"{debouncedSearch}"</strong>
                </span>
                <button onClick={() => setSearchTerm('')} className="hover:bg-purple-200 rounded-full p-0.5">
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            )}
            {filter !== 'all' && (
              <div className="px-3 py-1 bg-blue-100 border border-blue-300 rounded-full text-sm text-blue-800 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                <span>Filtre: <strong>{filter}</strong></span>
                <button onClick={() => setFilter('all')} className="hover:bg-blue-200 rounded-full p-0.5">
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className="px-3 py-1 bg-green-100 border border-green-300 rounded-full text-sm text-green-800">
              ✅ <strong>{totalItems.toLocaleString()}</strong> résultat{totalItems > 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>

      {/* Indicateur de page */}
      {totalPages > 1 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
          📄 Page <strong>{currentPage}</strong> sur <strong>{totalPages}</strong>
          {totalItems > 0 && ` • Affichage de ${((currentPage - 1) * itemsPerPage) + 1} à ${Math.min(currentPage * itemsPerPage, totalItems)} sur ${totalItems.toLocaleString()} clients`}
        </div>
      )}

      {/* Liste des clients */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader className="w-8 h-8 animate-spin text-purple-600 mb-3" />
            <span className="text-gray-600">Recherche en cours...</span>
            {debouncedSearch && (
              <span className="text-sm text-gray-500 mt-1">
                {searchMode === 'meter' ? `Compteur: "${debouncedSearch}"` : `Recherche: "${debouncedSearch}"`}
              </span>
            )}
          </div>
        ) : customers.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-lg font-medium">Aucun client trouvé</p>
            {debouncedSearch && (
              <div className="mt-4 space-y-2">
                <p className="text-sm">
                  {searchMode === 'meter'
                    ? <>Aucun compteur avec le numéro <strong>"{debouncedSearch}"</strong></>
                    : <>Aucun résultat pour <strong>"{debouncedSearch}"</strong></>
                  }
                </p>
                <button onClick={() => setSearchTerm('')} className="text-purple-600 hover:text-purple-800 underline text-sm">
                  Effacer la recherche
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Client</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Téléphone</th>
                    {/* ✅ Colonne compteur uniquement en mode recherche compteur */}
                    {searchMode === 'meter' && debouncedSearch && (
                      <th className="text-left py-3 px-4 text-sm font-semibold text-purple-700 bg-purple-50">
                        <Hash className="w-4 h-4 inline mr-1" />
                        N° Compteur
                      </th>
                    )}
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Score</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                      <Clock className="w-4 h-4 inline mr-1" />Dernier Achat
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                      <Zap className="w-4 h-4 inline mr-1" />Consommation
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                      <DollarSign className="w-4 h-4 inline mr-1" />Impayés
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {customers.map((customer, index) => (
                    <tr key={`${customer.customer_id}-${customer.meter_id || ''}-${index}`} className="hover:bg-purple-50 transition-colors">
                      <td className="py-3 px-4 font-mono text-sm font-semibold text-gray-900">
                        {customer.customer_id}
                      </td>
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">{customer.full_name}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{customer.phone}</td>

                      {/* ✅ Colonne compteur en mode meter : affiche uniquement LE compteur qui correspond */}
                      {searchMode === 'meter' && debouncedSearch && (
                        <td className="py-3 px-4 bg-purple-50">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 text-xs rounded font-mono font-bold bg-purple-200 text-purple-900 ring-1 ring-purple-400">
                              {customer.meter_id}
                            </span>
    
                          </div>
                        </td>
                      )}

                      <td className="py-3 px-4">{getRiskBadge(customer.risk_score)}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {customer.score_details?.details?.last_purchase_days_ago != null
                          ? `${customer.score_details.details.last_purchase_days_ago}j`
                          : 'Jamais'}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {customer.score_details?.details?.avg_monthly_kwh?.toFixed(1) || '0'} kWh/m
                      </td>
                      <td className="py-3 px-4 text-sm font-semibold text-red-600">
                        {customer.score_details?.details?.unpaid_fees?.toFixed(0) || '0'} F
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => { setSelectedCustomer(customer); setShowDetailModal(true); }}
                            className="p-1.5 text-purple-600 hover:bg-purple-100 rounded transition-colors"
                            title="Voir détails"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => handleSendSMS(customer)}
                            className="p-1.5 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                            title="Envoyer SMS"
                            disabled={!customer.phone}
                          >
                            <MessageSquare size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Affichage de <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> à{' '}
                    <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span> sur{' '}
                    <span className="font-medium">{totalItems.toLocaleString()}</span> clients
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={goToPrevPage} disabled={currentPage === 1} className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1">
                      <ChevronLeft className="w-4 h-4" />Précédent
                    </button>
                    <div className="flex items-center gap-1">
                      {getPageNumbers.map((page, index) =>
                        typeof page === 'number' ? (
                          <button key={`page-${page}`} onClick={() => goToPage(page)} className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${currentPage === page ? 'bg-purple-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-100'}`}>
                            {page}
                          </button>
                        ) : (
                          <span key={`ellipsis-${index}`} className="px-2 text-gray-500">{page}</span>
                        )
                      )}
                    </div>
                    <button onClick={goToNextPage} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1">
                      Suivant<ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal Détails */}
      {showDetailModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">{selectedCustomer.full_name}</h2>
                  <p className="text-purple-100 mt-1">ID: {selectedCustomer.customer_id}</p>
                  {/* ✅ Afficher les compteurs dans la modale de détails */}
                  {selectedCustomer.meters && selectedCustomer.meters.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {selectedCustomer.meters.map((m, i) => (
                        <span key={i} className="px-2 py-0.5 bg-white/20 rounded text-xs font-mono">
                          🔌 {m.meter_id}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={() => setShowDetailModal(false)} className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors">
                  <XCircle size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200">
                <span className="font-semibold text-gray-900 text-lg">Score de Risque:</span>
                {getRiskBadge(selectedCustomer.risk_score)}
              </div>

              {selectedCustomer.score_details?.reasons && selectedCustomer.score_details.reasons.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Raisons du Score de Risque
                  </h3>
                  <ul className="space-y-2">
                    {selectedCustomer.score_details.reasons.map((reason, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-red-800">
                        <TrendingDown className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        {reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedCustomer.score_details?.details && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4 text-lg">Analyse Comportementale</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4 text-blue-600" />
                        <p className="text-xs font-medium text-blue-600">Dernier achat</p>
                      </div>
                      <p className="text-2xl font-bold text-blue-900">{selectedCustomer.score_details.details.last_purchase_days_ago || '∞'}</p>
                      <p className="text-xs text-blue-700 mt-1">jours</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-4 h-4 text-green-600" />
                        <p className="text-xs font-medium text-green-600">Achats (3 mois)</p>
                      </div>
                      <p className="text-2xl font-bold text-green-900">{selectedCustomer.score_details.details.purchases_last_3months}</p>
                      <p className="text-xs text-green-700 mt-1">transactions</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-4 h-4 text-purple-600" />
                        <p className="text-xs font-medium text-purple-600">Montant moyen/mois</p>
                      </div>
                      <p className="text-2xl font-bold text-purple-900">{selectedCustomer.score_details.details.avg_monthly_amount?.toFixed(0)}</p>
                      <p className="text-xs text-purple-700 mt-1">FCFA</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg border border-yellow-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-yellow-600" />
                        <p className="text-xs font-medium text-yellow-600">Consommation moy/mois</p>
                      </div>
                      <p className="text-2xl font-bold text-yellow-900">{selectedCustomer.score_details.details.avg_monthly_kwh?.toFixed(1)}</p>
                      <p className="text-xs text-yellow-700 mt-1">kWh</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-lg border border-red-200">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-red-600" />
                        <p className="text-xs font-medium text-red-600">Alertes actives</p>
                      </div>
                      <p className="text-2xl font-bold text-red-900">{selectedCustomer.score_details.details.unresolved_alerts}</p>
                      <p className="text-xs text-red-700 mt-1">non résolues</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg border border-orange-200">
                      <div className="flex items-center gap-2 mb-2">
                        <XCircle className="w-4 h-4 text-orange-600" />
                        <p className="text-xs font-medium text-orange-600">Redevances impayées</p>
                      </div>
                      <p className="text-2xl font-bold text-orange-900">{selectedCustomer.score_details.details.unpaid_fees?.toFixed(0)}</p>
                      <p className="text-xs text-orange-700 mt-1">FCFA</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-200">
                <button onClick={() => { setShowDetailModal(false); handleSendSMS(selectedCustomer); }} className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium">
                  <MessageSquare className="w-5 h-5" />SMS
                </button>
                <button onClick={() => handleQuickAction('block', selectedCustomer)} className="px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 font-medium">
                  <Ban className="w-5 h-5" />Bloquer
                </button>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonitoringClients;